import { Link } from 'react-router-dom';
import { ArrowRight, Check, Award } from 'lucide-react';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import AffiliateBanner from '../components/shared/AffiliateBanner';
import CTABox from '../components/shared/CTABox';
import TableOfContents from '../components/shared/TableOfContents';
import StarRating from '../components/shared/StarRating';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const RegisteredAgentPage = () => {
  const tocItems = [
    { id: 'was-ist-ra', title: 'Was ist ein Registered Agent?', level: 2 },
    { id: 'warum-brauchen', title: 'Warum brauchen Sie einen?', level: 2 },
    { id: 'selbst-sein', title: 'Selbst Registered Agent sein?', level: 2 },
    { id: 'worauf-achten', title: 'Worauf achten bei der Auswahl?', level: 2 },
    { id: 'vergleich', title: 'Die besten Services 2026', level: 2 },
    { id: 'testsieger', title: 'Unser Testsieger', level: 2 },
    { id: 'faq', title: 'Häufig gestellte Fragen', level: 2 },
  ];

  const providers = [
    { 
      name: 'Northwest Registered Agent', 
      rating: 4.9, 
      price: '$125/Jahr', 
      firstYear: '1. Jahr kostenlos (bei Gründung)',
      highlights: 'Testsieger — Datenschutz, eigene Adresse, US-Support',
      isWinner: true 
    },
    { 
      name: 'Registered Agents Inc.', 
      rating: 4.3, 
      price: '$199/Jahr', 
      firstYear: '-',
      highlights: 'Solide, aber teurer',
      isWinner: false 
    },
    { 
      name: 'ZenBusiness', 
      rating: 4.1, 
      price: '$199/Jahr', 
      firstYear: '-',
      highlights: 'Gutes Gesamtpaket',
      isWinner: false 
    },
    { 
      name: 'Incfile', 
      rating: 3.8, 
      price: '$119/Jahr', 
      firstYear: '-',
      highlights: 'Günstig, aber weniger Datenschutz',
      isWinner: false 
    },
    { 
      name: 'LegalZoom', 
      rating: 3.5, 
      price: '$299/Jahr', 
      firstYear: '-',
      highlights: 'Bekannte Marke, überteuert',
      isWinner: false 
    },
  ];

  const faqs = [
    {
      question: 'Was passiert, wenn ich keinen Registered Agent habe?',
      answer: 'Ohne Registered Agent verliert Ihre LLC ihren "Good Standing"-Status. Der Staat kann Ihre LLC als nicht konform markieren, Strafen verhängen oder sie sogar administrativ auflösen. Außerdem können Sie keine rechtlichen Dokumente empfangen.'
    },
    {
      question: 'Kann ich meinen Registered Agent wechseln?',
      answer: 'Ja, ein Wechsel ist jederzeit möglich. Sie müssen lediglich ein Formular beim Secretary of State einreichen, das Ihren neuen Registered Agent benennt. Die meisten Services übernehmen diesen Prozess für Sie.'
    },
    {
      question: 'Muss mein Registered Agent im gleichen Staat wie meine LLC sein?',
      answer: 'Ja, Ihr Registered Agent muss eine physische Adresse in dem Bundesstaat haben, in dem Ihre LLC registriert ist. Wenn Sie LLCs in mehreren Staaten haben, benötigen Sie in jedem Staat einen Registered Agent (oder einen Service, der alle Staaten abdeckt).'
    },
    {
      question: 'Was macht ein Registered Agent genau?',
      answer: 'Ein Registered Agent empfängt offizielle Dokumente für Ihre LLC: Service of Process (rechtliche Zustellungen), Steuerunterlagen vom Staat, Jahresberichte-Erinnerungen und andere behördliche Korrespondenz. Er leitet diese Dokumente an Sie weiter.'
    },
    {
      question: 'Sind Registered Agent Services für Ausländer verfügbar?',
      answer: 'Ja, gerade für internationale LLC-Inhaber sind Registered Agent Services essentiell, da Sie selbst keine US-Adresse haben. Die meisten großen Anbieter haben Erfahrung mit internationalen Kunden.'
    },
    {
      question: 'Wie schnell werden Dokumente weitergeleitet?',
      answer: 'Die meisten Registered Agent Services scannen eingehende Dokumente am selben Tag und stellen sie Ihnen digital zur Verfügung. Bei zeitkritischen rechtlichen Zustellungen ist dies besonders wichtig.'
    },
  ];

  return (
    <div className="py-12" data-testid="registered-agent-page">
      <div className="container-wide">
        <Breadcrumbs items={[{ label: 'Registered Agent', href: '/registered-agent' }]} />
        
        <AffiliateBanner />

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="prose-reviewlex">
              {/* Hero */}
              <header className="mb-12">
                <h1 className="mb-6">Registered Agent — Was ist das und warum brauchen Sie einen?</h1>
                <p className="text-xl text-slate-600 mb-6">
                  Ein Registered Agent ist eine gesetzliche Anforderung für jede US-LLC. In diesem 
                  Guide erklären wir, was ein Registered Agent macht, worauf Sie bei der Auswahl 
                  achten sollten und vergleichen die besten Services für 2026.
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>Aktualisiert: April 2026</span>
                  <span>•</span>
                  <span>Lesezeit: 10 Min.</span>
                </div>
              </header>

              {/* Was ist ein RA */}
              <section id="was-ist-ra" className="mb-16">
                <h2>Was ist ein Registered Agent?</h2>
                <p>
                  Ein <strong>Registered Agent</strong> (auch "Statutory Agent" oder "Agent for Service of Process" genannt) 
                  ist eine Person oder ein Unternehmen, das offiziell beauftragt ist, rechtliche und behördliche Dokumente 
                  im Namen Ihrer LLC zu empfangen.
                </p>
                <p>
                  Jeder US-Bundesstaat verlangt, dass LLCs und Corporations einen Registered Agent benennen, der während 
                  der normalen Geschäftszeiten an einer physischen Adresse im Gründungsstaat erreichbar ist.
                </p>
                
                <div className="card-flat p-6 my-8">
                  <h4 className="font-heading font-bold text-navy-900 mb-3">Ein Registered Agent empfängt:</h4>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-1 flex-shrink-0" />
                      <span><strong>Service of Process:</strong> Klagen und rechtliche Zustellungen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-1 flex-shrink-0" />
                      <span><strong>Staatliche Korrespondenz:</strong> Mitteilungen vom Secretary of State</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-1 flex-shrink-0" />
                      <span><strong>Steuerdokumente:</strong> Benachrichtigungen der Steuerbehörden</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-1 flex-shrink-0" />
                      <span><strong>Compliance-Erinnerungen:</strong> Fristen für Jahresberichte</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Warum brauchen */}
              <section id="warum-brauchen" className="mb-16">
                <h2>Warum brauchen Sie einen Registered Agent?</h2>
                
                <h3 className="mt-8 mb-4">1. Gesetzliche Pflicht</h3>
                <p>
                  Es ist keine Option — jede LLC in allen 50 US-Bundesstaaten muss einen Registered Agent haben. 
                  Ohne gültigen Registered Agent kann Ihre LLC ihren "Good Standing"-Status verlieren.
                </p>

                <h3 className="mt-8 mb-4">2. Datenschutz</h3>
                <p>
                  Die Adresse Ihres Registered Agents wird in öffentlichen Registern eingetragen. Ein professioneller 
                  Service bedeutet, dass Ihre persönliche Adresse privat bleibt. Besonders wichtig für Unternehmer, 
                  die von zu Hause arbeiten.
                </p>

                <h3 className="mt-8 mb-4">3. Nie ein wichtiges Dokument verpassen</h3>
                <p>
                  Rechtliche Zustellungen haben oft enge Fristen. Wenn Sie ein Dokument verpassen (z.B. weil Sie 
                  reisen oder umgezogen sind), kann das schwerwiegende Folgen haben. Ein Registered Agent ist 
                  immer erreichbar.
                </p>

                <h3 className="mt-8 mb-4">4. Professionelles Erscheinungsbild</h3>
                <p>
                  Eine professionelle Registered Agent Adresse wirkt seriöser als eine Privatadresse — besonders 
                  wichtig bei Geschäftspartnern und Banken.
                </p>
              </section>

              {/* Selbst sein */}
              <section id="selbst-sein" className="mb-16">
                <h2>Kann ich mein eigener Registered Agent sein?</h2>
                <p>
                  Technisch ja — wenn Sie die Anforderungen erfüllen:
                </p>
                <ul>
                  <li>Sie haben eine physische Adresse in dem Bundesstaat, in dem Ihre LLC registriert ist (keine PO Box)</li>
                  <li>Sie sind während der normalen Geschäftszeiten (typisch 9-17 Uhr, Mo-Fr) an dieser Adresse erreichbar</li>
                  <li>Sie sind mindestens 18 Jahre alt</li>
                </ul>
                
                <div className="card-flat p-6 my-8 border-l-4 border-l-amber-500 bg-amber-50">
                  <h4 className="font-heading font-bold text-navy-900 mb-2">Warum das für internationale Gründer keine Option ist:</h4>
                  <p className="text-slate-700 text-sm mb-0">
                    Als Nicht-US-Resident haben Sie keine physische Adresse in den USA und können nicht während 
                    US-Geschäftszeiten persönlich verfügbar sein. Ein professioneller Registered Agent Service 
                    ist daher für internationale LLC-Inhaber die einzige praktikable Option.
                  </p>
                </div>
              </section>

              {/* Worauf achten */}
              <section id="worauf-achten" className="mb-16">
                <h2>Worauf achten bei der Auswahl?</h2>
                
                <div className="space-y-4">
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">✓ Physische Adresse im richtigen Staat</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Der Registered Agent muss eine echte Straßenadresse (keine PO Box) in Ihrem Gründungsstaat haben.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">✓ Erreichbarkeit während Geschäftszeiten</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Der Service muss während der normalen Geschäftszeiten verfügbar sein, um Dokumente zu empfangen.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">✓ Digitale Dokumentenzustellung</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Gute Services scannen eingehende Dokumente und stellen sie Ihnen digital zur Verfügung — idealerweise am selben Tag.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">✓ Compliance-Erinnerungen</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Automatische Benachrichtigungen für fällige Jahresberichte und andere Fristen.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">✓ Transparente Preise</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Keine versteckten Gebühren oder Upsells. Der Preis sollte alle Grundleistungen enthalten.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">✓ Datenschutz</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Idealerweise verwendet der Service seine eigene Adresse auf öffentlichen Dokumenten — nicht Ihre persönliche.
                    </p>
                  </div>
                </div>
              </section>

              {/* Vergleich */}
              <section id="vergleich" className="mb-16">
                <h2>Die besten Registered Agent Services 2026 — Unser Test</h2>
                <p>
                  Wir haben die führenden Registered Agent Services getestet und verglichen. 
                  Hier unsere Ergebnisse:
                </p>

                <div className="overflow-x-auto my-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Anbieter</TableHead>
                        <TableHead>Bewertung</TableHead>
                        <TableHead>Preis/Jahr</TableHead>
                        <TableHead>Erstes Jahr</TableHead>
                        <TableHead>Highlights</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {providers.map((provider, index) => (
                        <TableRow 
                          key={index}
                          className={provider.isWinner ? 'bg-teal-50 border-l-4 border-l-teal-600' : ''}
                          data-testid={`table-row-${provider.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {provider.name}
                              {provider.isWinner && (
                                <span className="badge-teal text-[10px]">Testsieger</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <StarRating rating={provider.rating} size="sm" showValue={false} />
                              <span className="text-sm">{provider.rating}/5</span>
                            </div>
                          </TableCell>
                          <TableCell>{provider.price}</TableCell>
                          <TableCell className="text-sm">{provider.firstYear}</TableCell>
                          <TableCell className="text-sm text-slate-600">{provider.highlights}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </section>

              {/* Testsieger */}
              <section id="testsieger" className="mb-16">
                <h2>Unser Testsieger: Northwest Registered Agent — Ausführlicher Testbericht</h2>
                
                <div className="card-highlight p-8 mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="badge-teal flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      Testsieger 2026
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-navy-900 mb-4">
                    Northwest Registered Agent
                  </h3>
                  <StarRating rating={4.9} size="lg" />
                  
                  <div className="mt-6 mb-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-3">Was wir getestet haben:</h4>
                    <ul className="space-y-2 text-slate-700">
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Signup-Prozess:</strong> Schnell und unkompliziert, in unter 10 Minuten erledigt</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Dashboard:</strong> Übersichtlich, alle Dokumente sofort zugänglich</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Mail-Handling:</strong> Dokumente wurden am selben Tag gescannt und zugestellt</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Support:</strong> Durchschnittliche Telefonwartezeit: 4 Minuten, kompetente Antworten</span>
                      </li>
                    </ul>
                  </div>

                  <h4 className="font-heading font-bold text-navy-900 mb-3">Preisübersicht:</h4>
                  <ul className="space-y-1 text-sm mb-6">
                    <li>• Registered Agent Service: <strong>$125/Jahr</strong></li>
                    <li>• Bei LLC-Gründung über Northwest: <strong>1. Jahr kostenlos</strong></li>
                    <li>• Mail Forwarding (optional): $125/Jahr extra</li>
                  </ul>

                  <h4 className="font-heading font-bold text-navy-900 mb-3">Vorteile:</h4>
                  <ul className="space-y-1 text-sm text-teal-800 mb-4">
                    <li>✓ Verwendet eigene Adresse auf allen öffentlichen Dokumenten</li>
                    <li>✓ US-basierter Kundenservice mit echtem Fachwissen</li>
                    <li>✓ Schnelle digitale Dokumentenzustellung</li>
                    <li>✓ Über 25 Jahre Erfahrung, 3+ Mio. Kunden</li>
                  </ul>

                  <h4 className="font-heading font-bold text-navy-900 mb-3">Nachteile:</h4>
                  <ul className="space-y-1 text-sm text-red-700 mb-6">
                    <li>✗ Nicht der absolut günstigste Anbieter</li>
                    <li>✗ Keine deutsche Sprachoption</li>
                  </ul>

                  <h4 className="font-heading font-bold text-navy-900 mb-3">Unser Fazit:</h4>
                  <p className="text-slate-700 mb-6">
                    Northwest Registered Agent bietet das beste Gesamtpaket aus Datenschutz, Service-Qualität 
                    und fairem Preis. Die Kombination aus kostenlosem ersten Jahr (bei LLC-Gründung) und 
                    echtem Datenschutz macht sie zu unserer klaren Empfehlung.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="https://www.northwestregisteredagent.com"
                      target="_blank"
                      rel="nofollow sponsored noopener noreferrer"
                      className="cta-primary"
                      data-testid="ra-page-cta"
                    >
                      Northwest Registered Agent testen
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                    <Link to="/northwest-registered-agent-erfahrungen" className="cta-secondary">
                      Vollständigen Testbericht lesen
                    </Link>
                  </div>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-16">
                <h2>Häufig gestellte Fragen</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left font-heading font-semibold text-navy-900">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>

            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="space-y-8">
              <TableOfContents items={tocItems} />
              
              <CTABox
                variant="sidebar"
                highlights={[
                  '$125/Jahr (1. Jahr kostenlos)',
                  'Datenschutz inklusive',
                  'Alle 50 US-Staaten',
                  'Digitale Dokumentenzustellung'
                ]}
              />

              <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
                <h4 className="font-heading font-bold text-navy-900 mb-4">Verwandte Ratgeber</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/llc-usa-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → LLC Gründung USA
                    </Link>
                  </li>
                  <li>
                    <Link to="/ratgeber/registered-agent-wechseln" className="text-sm text-slate-700 hover:text-teal-600">
                      → Registered Agent wechseln
                    </Link>
                  </li>
                  <li>
                    <Link to="/northwest-registered-agent-erfahrungen" className="text-sm text-slate-700 hover:text-teal-600">
                      → Northwest Testbericht
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default RegisteredAgentPage;
